import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MapPin,
  ChevronRight,
  Search,
  GraduationCap,
  Info,
  X,
  ArrowRight,
  Check,
} from "lucide-react";

import SchoolProfile from "./SchoolProfile";
import AppFeedbackModal from "./AppFeedbackModal";
import NearbyAthleticsModal from "./NearbyAthleticsModal";
import NearbyStemModal from "./NearbyStemModal";
import { useAuth } from "../lib/auth";
import { resolveCollegeLogo, COLLEGE_LOGO_FALLBACK } from "../lib/collegeLogos";
import { schoolAPI } from "../services/api";
import MoreToolsToTry from "./MoreToolsToTry";

/* ========================================================================
   UTILITY FUNCTIONS
   ======================================================================== */
const formatScore = (value, fallback = "--") => {
  if (value === null || value === undefined) return fallback;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return fallback;
  return Math.round(numeric);
};

const formatSnapgradScore = (value, fallback = "--") => {
  if (value === null || value === undefined) return fallback;
  const raw = typeof value === "string" ? value.split("/")[0].trim() : value;
  const numeric = Number(raw);
  if (Number.isNaN(numeric)) return fallback;
  return Number.isInteger(numeric) ? numeric.toString() : numeric.toFixed(1);
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "--";
  const numeric =
    typeof value === "string"
      ? Number(value.replace(/[^0-9.-]/g, ""))
      : Number(value);
  if (Number.isNaN(numeric)) return "--";
  return `$${numeric.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatRatio = (value) => {
  if (value === null || value === undefined) return "--";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return "--";
  return Number.isInteger(numeric) ? numeric.toString() : numeric.toFixed(1);
};

const PREFETCH_INITIAL_COUNT = 8;
const PREFETCH_CONCURRENCY = 2;
const PREFETCH_ROOT_MARGIN = "800px 0px";
const PREFETCH_NEARBY_COUNT = 6;
const NEARBY_DEFAULT_RADIUS = 50;
const NEARBY_DEFAULT_LIMIT = 20;
const NEARBY_STEM_LIMIT = 10;
const INITIAL_VISIBLE_SCHOOLS = 12;
const VISIBLE_SCHOOLS_INCREMENT = 12;

const normalizeSchoolDetails = (school, data) => {
  const normalized = { ...data };

  if (school?.source === "athletes") {
    const percentFields = [
      "college_enrollment",
      "college_enrollment_rate",
      "four_year_matriculation_rate",
      "graduation_rate",
    ];
    percentFields.forEach((field) => {
      const value = normalized[field];
      if (typeof value === "number" && value <= 1.2) {
        normalized[field] = value * 100;
      }
    });
  }

  return normalized;
};

/* ========================================================================
   CONSTANTS & CONFIG
   ======================================================================== */
const curatedDescriptions = {
  stem: {
    label: "Curated collection",
    heading: (count) => `${count} Standout STEM Schools`,
    description:
      "Explore high schools that excel in science, technology, engineering, and math readiness.",
  },
  athletes: {
    label: "Curated collection",
    heading: (count) => `${count} Athlete-Friendly Schools`,
    description:
      "Discover schools celebrated for athletics programs, facilities, and supportive sports culture.",
  },
};

const COMPARISON_SECTIONS = [
  {
    id: "basic",
    heading: "Basic information",
    info: "Key facts about location, academics, and overall performance.",
    rows: [
      { key: "location", label: "Location" },
      { key: "satAverage", label: "SAT average" },
      { key: "graduationRate", label: "Graduation rate" },
      { key: "studentTeacherRatio", label: "Student-teacher ratio" },
      { key: "overallGrade", label: "Overall grade" },
    ],
  },
  {
    id: "colleges-majors",
    heading: "Colleges and majors",
    info: "Shows the colleges students most often aim for after graduation and the majors they’re most interested in studying.",
    rows: [
      { key: "topColleges", label: "Top colleges" },
      { key: "topMajors", label: "Top majors" },
      { key: "collegeEnrollment", label: "College enrollment" },
    ],
  },
  {
    id: "financial",
    heading: "Financial Details (Avg Tuition at Top Colleges)",
    info: "Average tuition and financial aid at top destination colleges.",
    rows: [
      { key: "tuitionInState", label: "Avg Tuition (In-State)" },
      { key: "tuitionOutOfState", label: "Avg Tuition (Out-of-State)" },
      { key: "avgFinancialAid", label: "Avg Financial Aid" },
    ],
  },
  {
    id: "athletics",
    heading: "Athletic achievements",
    info: "Highlights of athletic accomplishments reported for this school, including titles, honors, and notable sports milestones.",
    rows: [{ key: "topPrograms", label: "Top achievements" }],
  },
];

/* ========================================================================
   MAIN COMPONENT
   ======================================================================== */
const SchoolsSection = ({
  schools,
  isSearched,
  summary,
  mode = "overview",
  activeLocation = null,
  locationError = null,
  onChangeLocation,
  onRequireAuth,
}) => {
  const [searchTermByView, setSearchTermByView] = useState({
    overview: "",
    stem: "",
    athletes: "",
  });
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNearbyAthleticsOpen, setIsNearbyAthleticsOpen] = useState(false);
  const [isNearbyStemOpen, setIsNearbyStemOpen] = useState(false);
  const [nearbyOriginSchool, setNearbyOriginSchool] = useState(null);
  const [nearbyStemOriginSchool, setNearbyStemOriginSchool] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [viewedDetailsCount, setViewedDetailsCount] = useState(0);
  const [nearbyAthleticsCache, setNearbyAthleticsCache] = useState({});
  const [nearbyStemCache, setNearbyStemCache] = useState({});

  // Comparison State
  const [compareMode, setCompareMode] = useState(false);
  const [selectedCompareSchools, setSelectedCompareSchools] = useState([]);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [comparisonDetails, setComparisonDetails] = useState({});
  const [prefetchedProfiles, setPrefetchedProfiles] = useState({});
  const prefetchingIdsRef = useRef(new Set());
  const prefetchQueueRef = useRef([]);
  const activePrefetchesRef = useRef(0);
  const pendingProfileFetchesRef = useRef(new Map());
  const nearbyFetchesRef = useRef(new Set());
  const nearbyStemFetchesRef = useRef(new Set());
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_SCHOOLS);
  const sentinelRef = useRef(null);

  const { userEmail, userName } = useAuth();
  const feedbackKey = userEmail
    ? `appFeedbackSubmitted:${userEmail}`
    : "appFeedbackSubmitted:guest";
  const feedbackViewsKey = userEmail
    ? `appFeedbackViews:${userEmail}`
    : "appFeedbackViews:guest";

  const curatedCopy = curatedDescriptions[mode];
  const isCuratedView = Boolean(curatedCopy);
  const isAthletesView = mode === "athletes";
  const isStemView = mode === "stem";
  const toTitleCase = (value = "") =>
    value
      .replace(/\uFFFD/g, "")
      .split(/\s+/)
      .map((word) => {
        if (!word) return "";
        const cleaned = word.replace(/[^a-zA-Z]/g, "");
        if (cleaned.length === 2) return word.toUpperCase();
        return word[0].toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(" ")
      .replace(/\s{2,}/g, " ")
      .trim();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedSubmitted = window.localStorage.getItem(feedbackKey);
    setFeedbackSubmitted(storedSubmitted === "true");

    const storedViews = window.localStorage.getItem(feedbackViewsKey);
    const parsedViews = storedViews ? Number(storedViews) : 0;
    setViewedDetailsCount(Number.isNaN(parsedViews) ? 0 : parsedViews);
  }, [feedbackKey, feedbackViewsKey]);

  const activeSearchTerm = searchTermByView[mode] ?? "";

  const hasActiveLocation =
    Number.isFinite(Number(activeLocation?.lat)) &&
    Number.isFinite(Number(activeLocation?.lng));
  const stemLabel = activeLocation?.label || "Your Search";
  const stemHeading = hasActiveLocation
    ? `Top STEM Schools Near ${stemLabel}`
    : "Top STEM Schools";
  const stemDescription = hasActiveLocation
    ? "Sorted by distance from your search."
    : "Search a city, ZIP, or address to sort by distance.";
  const athleticsHeading = hasActiveLocation
    ? `Top Athletics schools near ${stemLabel}`
    : "Top Athletics schools";
  const athleticsDescription = hasActiveLocation
    ? "Sorted by distance from your search."
    : "Search a city, ZIP, or address to sort by distance.";

  const filteredSchools = useMemo(() => {
    return schools.filter((school) => {
      const matchesSearch = school.school_name
        ?.toLowerCase()
        .includes(activeSearchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [schools, activeSearchTerm]);

  const validSchools = useMemo(
    () => filteredSchools.filter((school) => Boolean(school?.school_name?.trim())),
    [filteredSchools]
  );

  useEffect(() => {
    setVisibleCount(Math.min(INITIAL_VISIBLE_SCHOOLS, validSchools.length));
  }, [validSchools.length]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    if (visibleCount >= validSchools.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleCount((prev) =>
              Math.min(
                validSchools.length,
                prev + VISIBLE_SCHOOLS_INCREMENT
              )
            );
          }
        });
      },
      { rootMargin: "400px 0px" }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [visibleCount, validSchools.length]);

  const visibleSchools = useMemo(
    () => validSchools.slice(0, visibleCount),
    [validSchools, visibleCount]
  );

  useEffect(() => {
    filteredSchools
      .slice(0, PREFETCH_INITIAL_COUNT)
      .forEach((school, index) =>
        enqueuePrefetch(school, { priority: index === 0 })
      );
  }, [filteredSchools]);

  const handleSchoolClick = (school) => {
    prefetchSchoolDetails(school, { force: true });
    setSelectedSchoolId(school.id);
    setSelectedSchool(school);
    setIsProfileOpen(true);
  };

  const handleNearbySchoolClick = (school) => {
    if (school?.id) {
      prefetchSchoolDetails(school, { force: true });
    }
    setSelectedSchoolId(school?.id ?? null);
    setSelectedSchool(school);
    setIsProfileOpen(true);
  };

  const handleCloseProfile = () => {
    setIsProfileOpen(false);
    setSelectedSchoolId(null);
    setSelectedSchool(null);
    const nextCount = viewedDetailsCount + 1;
    setViewedDetailsCount(nextCount);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(feedbackViewsKey, String(nextCount));
    }

    const shouldPrompt =
      !feedbackSubmitted && (nextCount === 1 || nextCount % 3 === 0);
    if (shouldPrompt) {
      setShowFeedbackModal(true);
    } else {
      setShowFeedbackModal(false);
    }
  };

  const handleOpenNearbyAthletics = (originSchool) => {
    if (!originSchool) return;
    prefetchNearbyAthletics(originSchool);
    setNearbyOriginSchool(originSchool);
    setIsNearbyAthleticsOpen(true);
  };

  const handleCloseNearbyAthletics = () => {
    setIsNearbyAthleticsOpen(false);
  };

  const handleOpenNearbyStem = (originSchool) => {
    if (!originSchool) return;
    prefetchNearbyStem(originSchool);
    setNearbyStemOriginSchool(originSchool);
    setIsNearbyStemOpen(true);
  };

  const handleCloseNearbyStem = () => {
    setIsNearbyStemOpen(false);
  };

  const handleFeedbackSubmitted = () => {
    setFeedbackSubmitted(true);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(feedbackKey, "true");
    }
    setShowFeedbackModal(false);
  };

  /* --- Comparison Logic --- */
  const compareLimitReached = selectedCompareSchools.length >= 4;

  const getNearbyCacheKey = (school) => {
    if (!school) return null;
    return (
      school.id ||
      `${school.school_name}-${school.address_zipcode ?? school.zip ?? ""}`
    );
  };

  const getNearbyCoords = (school) => {
    if (!school) return null;
    const lat =
      school.latitude ??
      school.coordinates?.latitude ??
      school.location?.latitude;
    const lon =
      school.longitude ??
      school.coordinates?.longitude ??
      school.location?.longitude;
    if (lat === null || lat === undefined || lon === null || lon === undefined) {
      return null;
    }
    const latitude = Number(lat);
    const longitude = Number(lon);
    if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
    return { latitude, longitude };
  };

  const prefetchNearbyAthletics = async (originSchool) => {
    const key = getNearbyCacheKey(originSchool);
    if (!key || nearbyAthleticsCache[key]) return;
    if (nearbyFetchesRef.current.has(key)) return;

    const coords = getNearbyCoords(originSchool);
    if (!coords) return;

    nearbyFetchesRef.current.add(key);
    try {
      const response = await schoolAPI.getNearbyAthleticsSchools({
        latitude: coords.latitude,
        longitude: coords.longitude,
        radius_miles: NEARBY_DEFAULT_RADIUS,
        limit: NEARBY_DEFAULT_LIMIT,
      });
      const nextSchools = response?.data?.schools ?? [];
      setNearbyAthleticsCache((prev) => ({ ...prev, [key]: nextSchools }));
    } catch (error) {
      console.warn("Failed to prefetch nearby athletics schools", error);
    } finally {
      nearbyFetchesRef.current.delete(key);
    }
  };

  const prefetchNearbyStem = async (originSchool) => {
    const key = getNearbyCacheKey(originSchool);
    if (!key || nearbyStemCache[key]) return;
    if (nearbyStemFetchesRef.current.has(key)) return;

    const coords = getNearbyCoords(originSchool);
    if (!coords) return;

    nearbyStemFetchesRef.current.add(key);
    try {
      const response = await schoolAPI.getNearbyStemSchools({
        latitude: coords.latitude,
        longitude: coords.longitude,
        radius_miles: NEARBY_DEFAULT_RADIUS,
        limit: NEARBY_STEM_LIMIT,
      });
      const nextSchools = response?.data?.schools ?? [];
      setNearbyStemCache((prev) => ({ ...prev, [key]: nextSchools }));
    } catch (error) {
      console.warn("Failed to prefetch nearby STEM schools", error);
    } finally {
      nearbyStemFetchesRef.current.delete(key);
    }
  };

  const handleComparisonToggle = (school) => {
    setSelectedCompareSchools((prev) => {
      const alreadySelected = prev.some((entry) => entry.id === school.id);
      if (alreadySelected) {
        return prev.filter((entry) => entry.id !== school.id);
      }
      if (prev.length >= 4) {
        return prev;
      }
      const nextList = [...prev, school];
      if (nextList.length === 4) {
        handleOpenComparisonModal();
      }
      fetchComparisonDetails(school);
      return nextList;
    });
  };

  const handleRemoveComparison = (schoolId) => {
    setSelectedCompareSchools((prev) =>
      prev.filter((entry) => entry.id !== schoolId)
    );
    setComparisonDetails((prev) => {
      const next = { ...prev };
      delete next[schoolId];
      return next;
    });
  };

  const fetchProfileDetails = async (school) => {
    if (!school?.id) return null;
    if (prefetchedProfiles[school.id]) return prefetchedProfiles[school.id];
    const pending = pendingProfileFetchesRef.current.get(school.id);
    if (pending) return pending;

    const run = (async () => {
      const params = Object.fromEntries(
        Object.entries({
          city: school.address_city,
          state: school.address_state,
          name: school.school_name,
          zipcode: school.address_zipcode,
          dataset: school.source,
        }).filter(([, value]) => value)
      );

      const response = await schoolAPI.getSchoolDetails(school.id, params);
      if (response.success) {
        const normalized = normalizeSchoolDetails(school, response.data);
        setPrefetchedProfiles((prev) => ({
          ...prev,
          [school.id]: normalized,
        }));
        return normalized;
      }
      return null;
    })();

    pendingProfileFetchesRef.current.set(school.id, run);
    try {
      const result = await run;
      return result;
    } finally {
      pendingProfileFetchesRef.current.delete(school.id);
    }
  };

  const prefetchSchoolDetails = async (school, { force = false } = {}) => {
    if (!school?.id) return;
    if (prefetchedProfiles[school.id]) return;
    if (prefetchingIdsRef.current.has(school.id) && !force) return;

    const runFetch = async () => {
      prefetchingIdsRef.current.add(school.id);
      activePrefetchesRef.current += 1;
      try {
        await fetchProfileDetails(school);
      } catch (error) {
        console.warn("Failed to prefetch detail", school.id, error);
      } finally {
        prefetchingIdsRef.current.delete(school.id);
        activePrefetchesRef.current = Math.max(
          0,
          activePrefetchesRef.current - 1
        );
        if (!force) {
          drainPrefetchQueue();
        }
      }
    };

    if (!force && activePrefetchesRef.current >= PREFETCH_CONCURRENCY) {
      prefetchQueueRef.current.push(school);
      return;
    }

    runFetch();
  };

  const drainPrefetchQueue = () => {
    while (
      activePrefetchesRef.current < PREFETCH_CONCURRENCY &&
      prefetchQueueRef.current.length > 0
    ) {
      const nextSchool = prefetchQueueRef.current.shift();
      prefetchSchoolDetails(nextSchool);
    }
  };

  const enqueuePrefetch = (school, { priority = false } = {}) => {
    if (!school?.id) return;
    if (prefetchedProfiles[school.id]) return;
    if (prefetchingIdsRef.current.has(school.id)) return;

    if (priority) {
      prefetchQueueRef.current.unshift(school);
    } else {
      prefetchQueueRef.current.push(school);
    }
    drainPrefetchQueue();
  };

  const handlePrefetchNearbySchools = (nearbySchools) => {
    if (!Array.isArray(nearbySchools) || nearbySchools.length === 0) return;
    nearbySchools
      .slice(0, PREFETCH_NEARBY_COUNT)
      .forEach((school, index) =>
        enqueuePrefetch(school, { priority: index === 0 })
      );
  };

  const fetchComparisonDetails = async (school) => {
    try {
      const normalized = await fetchProfileDetails(school);
      if (!normalized) return;
      setComparisonDetails((prev) => ({
        ...prev,
        [school.id]: normalized,
      }));
      setPrefetchedProfiles((prev) => ({
        ...prev,
        [school.id]: normalized,
      }));
    } catch (error) {
      console.warn("Failed to load comparison detail", school.id, error);
    }
  };

  const handleOpenComparisonModal = () => {
    if (!compareMode) {
      setCompareMode(true);
    }
    setIsCompareModalOpen(true);
  };

  const handleCloseComparisonModal = () => {
    setIsCompareModalOpen(false);
  };

  // If no search/curated view (landing state)
  if (!isSearched && !isCuratedView) {
    return (
      <div className="py-10">
        <div className="bg-white border border-[#EEE5DF] rounded-[32px] p-12 text-center shadow-sm">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF3ED] text-[#F07639]">
            <GraduationCap className="w-8 h-8" />
          </div>
          <h3 className="font-unbounded text-[28px] text-black mb-3">
            Your schools will appear here
          </h3>
          <p className="font-satoshi text-[18px] text-[#575757] max-w-xl mx-auto">
            Start by searching for a city, neighborhood, or address to see schools
            that match your college readiness goals.
          </p>
        </div>
        <SchoolProfile
          schoolId={selectedSchoolId}
          schoolSummary={selectedSchool}
          isOpen={isProfileOpen}
          onClose={handleCloseProfile}
          prefetchedProfile={
            selectedSchoolId ? prefetchedProfiles[selectedSchoolId] : null
          }
        />
        <NearbyAthleticsModal
          isOpen={isNearbyAthleticsOpen}
          onClose={handleCloseNearbyAthletics}
          originSchool={nearbyOriginSchool}
          onSelectSchool={handleNearbySchoolClick}
          onPrefetchSchools={handlePrefetchNearbySchools}
          prefetchedSchools={
            nearbyOriginSchool
              ? nearbyAthleticsCache[getNearbyCacheKey(nearbyOriginSchool)]
              : null
          }
          onCacheNearby={(origin, schools) => {
            const key = getNearbyCacheKey(origin);
            if (!key) return;
            setNearbyAthleticsCache((prev) => ({ ...prev, [key]: schools }));
          }}
        />
        <NearbyStemModal
          isOpen={isNearbyStemOpen}
          onClose={handleCloseNearbyStem}
          originSchool={nearbyStemOriginSchool}
          onSelectSchool={handleNearbySchoolClick}
          onPrefetchSchools={handlePrefetchNearbySchools}
          prefetchedSchools={
            nearbyStemOriginSchool
              ? nearbyStemCache[getNearbyCacheKey(nearbyStemOriginSchool)]
              : null
          }
          onCacheNearby={(origin, schools) => {
            const key = getNearbyCacheKey(origin);
            if (!key) return;
            setNearbyStemCache((prev) => ({ ...prev, [key]: schools }));
          }}
        />
      </div>
    );
  }

  const comparisonEntries = selectedCompareSchools.map((school) =>
    buildComparisonEntry({ ...school, ...comparisonDetails[school.id] })
  );

  return (
    <>
      <div className="space-y-8 pb-8 relative">
      {/* Header / Title Area */}
      <div className="bg-[#FFF8F4] border border-[#F2D5C7] rounded-[32px] p-8 lg:p-10 flex flex-col gap-6">
        <div
          // UPDATED: Changed to 'flex-col lg:flex-row' to FORCE side-by-side on desktop
          // and 'lg:items-center' to align the search bar vertically with the text block
          className="flex flex-col lg:flex-row lg:items-center justify-between gap-6"
        >
            {/* UPDATED: Added 'flex-1 min-w-0' to allow text to take up space but WRAP if needed */}
            <div className="flex-1 min-w-0">
                <p className="font-satoshi text-[16px] text-[#7C7C7C] uppercase tracking-[0.2em]">
                  {isCuratedView ? curatedCopy.label : "Search results"}
                </p>
                <h2
                  className={`font-unbounded text-black ${
                    isAthletesView
                      ? "text-[28px] sm:text-[30px] lg:text-[36px]"
                      : "text-[32px] lg:text-[40px]"
                  }`}
                >
                  {isCuratedView
                    ? isStemView
                      ? stemHeading
                      : isAthletesView
                      ? athleticsHeading
                      : curatedCopy.heading(
                          summary?.totalSchools ?? filteredSchools.length
                        )
                    : `${
                        summary?.totalSchools ?? filteredSchools.length
                      } Schools Near You`}
                </h2>
                <div className="flex flex-wrap items-center gap-3">
                  <p className="font-satoshi text-[18px] text-[#575757]">
                    {isCuratedView
                      ? isStemView
                        ? stemDescription
                        : isAthletesView
                        ? athleticsDescription
                        : curatedCopy.description
                      : summary?.query
                      ? `Showing results for "${toTitleCase(summary.query)}"`
                      : "Refine your search to discover the best fit."}
                  </p>
                  {(isStemView || isAthletesView) &&
                    hasActiveLocation &&
                    onChangeLocation && (
                    <button
                      type="button"
                      onClick={onChangeLocation}
                      className="text-sm font-semibold text-[#F07639] hover:text-[#d65e0c] underline underline-offset-4"
                    >
                      Change location
                    </button>
                  )}
                </div>
                {((isStemView || isAthletesView) &&
                  !hasActiveLocation &&
                  locationError) && (
                  <p className="mt-2 text-sm text-[#C2410C]">
                    {locationError}
                  </p>
                )}
              </div>

              {/* UPDATED: Added 'shrink-0' so the search bar maintains its width */}
              <div className="w-full lg:w-auto shrink-0">
                <label className="sr-only" htmlFor="school-search-input">
                  Filter schools by name
                </label>
                <div className="flex items-center gap-3 px-5 py-3 bg-white border border-[#E6D8CD] rounded-[30px] shadow-sm min-w-[280px] sm:min-w-[300px] md:min-w-[300px]">
                  <Search className="w-5 h-5 text-[#7C7C7C]" />
                  <input
                    id="school-search-input"
                    type="search"
                    placeholder="Search schools by name"
                    value={activeSearchTerm}
                    onChange={(event) =>
                      setSearchTermByView((prev) => ({
                        ...prev,
                        [mode]: event.target.value,
                      }))
                    }
                    className="w-full bg-transparent font-satoshi text-[18px] text-black placeholder:text-[#6B7280] placeholder:opacity-100 focus:outline-none"
                  />
                </div>
              </div>
        </div>
      </div>

      {/* Compare Widget (Top) */}
      <div className="bg-white border border-[#EEE5DF] rounded-[32px] p-6 shadow-sm flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h3 className="font-unbounded text-[22px] text-black">
              Compare High Schools
            </h3>
            <p className="text-sm text-[#575757]">
              You can always start a new search to add more schools to compare.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                if (!compareMode) {
                  setCompareMode(true);
                } else {
                  setCompareMode(false);
                  setSelectedCompareSchools([]);
                  setIsCompareModalOpen(false);
                }
              }}
              className="inline-flex items-center justify-center rounded-full bg-[#F07639] px-5 py-2 text-sm font-semibold text-white shadow-[0_10px_20px_rgba(240,116,24,0.35)] hover:bg-[#d65e0c] transition"
            >
              {compareMode ? "Clear selection" : "Compare schools"}
            </button>
            {selectedCompareSchools.length > 0 && (
              <button
                type="button"
                onClick={handleOpenComparisonModal}
                className="text-xs font-satoshi uppercase tracking-[0.3em] text-[#1F2937] underline underline-offset-4"
              >
                View selection
              </button>
            )}
            <span className="text-xs font-satoshi uppercase tracking-[0.3em] text-[#1F2937]">
              {selectedCompareSchools.length}/4 selected
            </span>
          </div>
        </div>

        {/* Selected Schools List (Inline) */}
        {selectedCompareSchools.length > 0 && (
          <div className="mt-2 border-t border-[#E4E6EE] pt-4">
            <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-8">
              <span className="shrink-0 text-[11px] font-satoshi font-bold uppercase tracking-[0.3em] text-[#9EA7B9] pt-1">
                Schools
              </span>
              <div className="flex flex-wrap items-center gap-1 text-sm font-bold uppercase leading-relaxed text-[#F07639]">
                {selectedCompareSchools.map((school, index) => (
                  <React.Fragment key={school.id}>
                    <span className="whitespace-normal md:whitespace-nowrap">
                      {school.school_name}
                    </span>
                    {index < selectedCompareSchools.length - 1 && (
                      <span className="mr-2 text-black/30">,</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}

        {compareLimitReached && (
          <p className="text-xs text-[#F0432F]">
            You have reached the maximum of four schools in a comparison.
          </p>
        )}
      </div>

      {/* Grid of School Cards */}
      {validSchools.length === 0 ? (
        <div className="bg-white border border-[#EEE5DF] rounded-[32px] p-12 text-center shadow-sm">
          <h3 className="font-unbounded text-[28px] text-black mb-3">
            No schools match your filters
          </h3>
          <p className="font-satoshi text-[18px] text-[#575757] max-w-2xl mx-auto">
            Try adjusting your search or clearing some filters to explore more
            schools in your area.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {visibleSchools.map((school) => {
            const isCompareSelected = selectedCompareSchools.some(
              (entry) => entry.id === school.id
            );
            const compareDisabled = compareLimitReached && !isCompareSelected;

            return (
              <SchoolListCard
                key={school.id}
                school={school}
                onSelect={() => handleSchoolClick(school)}
                compareMode={compareMode}
                isCompareSelected={isCompareSelected}
                compareDisabled={compareDisabled}
                onCompareToggle={() => handleComparisonToggle(school)}
                onPrefetch={() => enqueuePrefetch(school)}
              />
            );
          })}
          {visibleCount < validSchools.length && (
            <div ref={sentinelRef} className="h-1 col-span-full" />
          )}
        </div>
      )}

      {/* ----------------------------------------------------------- */}
      {/* FLOATING COMPARE ACTION BAR (Sticky Footer)              */}
      {/* ----------------------------------------------------------- */}
      {selectedCompareSchools.length > 0 && !isCompareModalOpen && (
        <div className="fixed bottom-8 left-1/2 z-[900] flex -translate-x-1/2 items-center gap-3 rounded-full border border-[#E7EDF5] bg-white p-3 pl-6 shadow-[0_20px_40px_rgba(0,0,0,0.2)] animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="flex flex-col pr-4">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF]">
              Selection
            </span>
            <span className="font-unbounded text-base text-[#0F172A]">
              <span className="text-[#F07639]">{selectedCompareSchools.length}</span> / 4
            </span>
          </div>

          <div className="h-10 w-[1px] bg-[#F3F4F6]" />

          <button
            onClick={handleOpenComparisonModal}
            className="inline-flex items-center gap-2 rounded-full bg-[#F07639] px-8 py-3.5 text-base font-bold text-white shadow-md transition-all hover:bg-[#d65e0c] hover:-translate-y-0.5 active:translate-y-0"
          >
            Compare Now
            <ArrowRight className="h-5 w-5" strokeWidth={3} />
          </button>
        </div>
      )}
      {/* ----------------------------------------------------------- */}

      {/* Modals */}
          <SchoolProfile
            schoolId={selectedSchoolId}
            schoolSummary={selectedSchool}
            isOpen={isProfileOpen}
            onClose={handleCloseProfile}
            prefetchedProfile={
              selectedSchoolId ? prefetchedProfiles[selectedSchoolId] : null
            }
          />
        <NearbyAthleticsModal
          isOpen={isNearbyAthleticsOpen}
          onClose={handleCloseNearbyAthletics}
          originSchool={nearbyOriginSchool}
          onSelectSchool={handleNearbySchoolClick}
          onPrefetchSchools={handlePrefetchNearbySchools}
          prefetchedSchools={
            nearbyOriginSchool
              ? nearbyAthleticsCache[getNearbyCacheKey(nearbyOriginSchool)]
              : null
          }
          onCacheNearby={(origin, schools) => {
            const key = getNearbyCacheKey(origin);
            if (!key) return;
            setNearbyAthleticsCache((prev) => ({ ...prev, [key]: schools }));
          }}
        />
        <NearbyStemModal
          isOpen={isNearbyStemOpen}
          onClose={handleCloseNearbyStem}
          originSchool={nearbyStemOriginSchool}
          onSelectSchool={handleNearbySchoolClick}
          onPrefetchSchools={handlePrefetchNearbySchools}
          prefetchedSchools={
            nearbyStemOriginSchool
              ? nearbyStemCache[getNearbyCacheKey(nearbyStemOriginSchool)]
              : null
          }
          onCacheNearby={(origin, schools) => {
            const key = getNearbyCacheKey(origin);
            if (!key) return;
            setNearbyStemCache((prev) => ({ ...prev, [key]: schools }));
          }}
        />
        <ComparisonModal
          open={isCompareModalOpen}
          entries={comparisonEntries}
          onClose={handleCloseComparisonModal}
          onRemove={handleRemoveComparison}
          onOpenNearbyAthletics={handleOpenNearbyAthletics}
          onOpenNearbyStem={handleOpenNearbyStem}
        />
      <AppFeedbackModal
        open={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSubmitted={handleFeedbackSubmitted}
        userEmail={userEmail || ""}
        userName={userName || ""}
        onRequireAuth={onRequireAuth}
      />
    </div>
    </>
  );
};

/* ========================================================================
   HELPER COMPONENTS & UTILS
   ======================================================================== */

const pickValue = (school, primaryKey, fallbackKey) => {
  if (school?.[primaryKey] !== undefined && school?.[primaryKey] !== null) {
    return school[primaryKey];
  }
  return school?.metrics?.[fallbackKey ?? primaryKey];
};

const pickSnapgradRating = (school) =>
  school?.Snapgrad_rating ??
  school?.snapgrad_rating ??
  school?.metrics?.Snapgrad_rating ??
  school?.metrics?.snapgrad_rating ??
  null;

const normalizeLabel = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  return value?.name ?? value?.label ?? null;
};

const buildComparisonEntry = (school) => {
  // Location
  const locationParts = [
    school.address_city ?? school.address?.city ?? "",
    school.address_state ?? school.address?.state ?? "",
    school.address_zipcode ?? school.address?.zipcode ?? "",
  ]
    .filter(Boolean)
    .map((part) => part.toUpperCase());
  const locationLabel = locationParts.join(", ");

  // Stats
  const graduationRaw =
    school.graduation_rate_percent ?? pickValue(school, "graduation_rate");
  const graduationRate =
    graduationRaw === null || graduationRaw === undefined
      ? "--"
      : `${formatScore(graduationRaw)}%`;

  const studentTeacherRatioValue =
    pickValue(school, "student_teacher_ratio") ??
    school.metrics?.student_teacher_ratio ??
    school.metrics?.studentTeacherRatio ??
    school.metrics?.studentteacher_ratio ??
    school.student_teacher_ratio ??
    school.studentTeacherRatio;
  const studentTeacherRatio =
    studentTeacherRatioValue === null || studentTeacherRatioValue === undefined
      ? "--"
      : formatRatio(studentTeacherRatioValue);

  const overallGrade =
    school.grade_overall ??
    school.overall_grade ??
    school.metrics?.grade_overall ??
    school.metrics?.overall_grade ??
    school.metrics?.grade ??
    school.metrics?.grade_level ??
    "--";

  // Lists
  const topCollegesList = (
    Array.isArray(school.top_colleges) ? school.top_colleges : []
  )
    .filter(Boolean)
    .slice(0, 3)
    .map(normalizeLabel)
    .filter(Boolean);

  const topMajorsList = (
    Array.isArray(school.top_majors) && school.top_majors.length > 0
      ? school.top_majors
      : Array.isArray(school.metrics?.top_majors)
      ? school.metrics.top_majors
      : []
  )
    .filter(Boolean)
    .slice(0, 3)
    .map(normalizeLabel)
    .filter(Boolean);

  const programCandidates = [
    ...(Array.isArray(school.top_programs) ? school.top_programs : []),
    ...(Array.isArray(school.metrics?.top_programs)
      ? school.metrics.top_programs
      : []),
    ...(Array.isArray(school.top_sports) ? school.top_sports : []),
    ...(Array.isArray(school.metrics?.top_sports)
      ? school.metrics.top_sports
      : []),
  ]
    .map(normalizeLabel)
    .filter(Boolean);
  const topPrograms = [...new Set(programCandidates)].slice(0, 4);

  // Financials
  const tuitionInStateValue =
    pickValue(school, "tuition_in_state") ??
    pickValue(school, "tuition") ??
    school.tuition ??
    school.metrics?.tuition_in_state;

  const tuitionOutOfStateValue =
    pickValue(school, "tuition_out_of_state") ??
    pickValue(school, "tuition_outstate") ??
    school.metrics?.tuition_out_of_state;

  const avgFinancialAidValue =
    pickValue(school, "avg_financial_aid") ??
    pickValue(school, "average_financial_aid") ??
    school.metrics?.avg_financial_aid ??
    school.metrics?.average_financial_aid;

  return {
    school,
    data: {
      location: locationLabel || "--",
      satAverage: formatScore(school.sat_average),
      graduationRate,
      studentTeacherRatio,
      overallGrade,
      topColleges: topCollegesList,
      topMajors: topMajorsList,
      collegeEnrollment: formatScore(
        pickValue(school, "college_enrollment"),
        "--"
      ),
      tuitionInState: formatCurrency(tuitionInStateValue),
      tuitionOutOfState: formatCurrency(tuitionOutOfStateValue),
      avgFinancialAid: formatCurrency(avgFinancialAidValue),
      topPrograms,
    },
  };
};

const truncationStyle = (lines) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
});

const renderComparisonValue = (
  value,
  { numbered = false, clampLines = 2 } = {}
) => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-[#9CA3AF]">--</span>;
    }
    return (
      <div className="space-y-1">
        {value.map((item, index) => (
          <p
            key={`${item ?? "item"}-${index}`}
            className="text-sm text-[#1F2937]"
            style={clampLines ? truncationStyle(clampLines) : undefined}
          >
            {numbered ? `${index + 1}. ${item}` : item}
          </p>
        ))}
      </div>
    );
  }
  return <span>{value ?? "--"}</span>;
};

/* ========================================================================
   MODAL COMPONENT (SCROLL FIXED)
   ======================================================================== */
const ComparisonModal = ({
  open,
  entries,
  onClose,
  onRemove,
  onOpenNearbyAthletics,
  onOpenNearbyStem,
}) => {
  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;

    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousOverscrollBehavior = body.style.overscrollBehavior;

    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";

    return () => {
      body.style.overflow = previousOverflow;
      body.style.overscrollBehavior = previousOverscrollBehavior;
    };
  }, [open]);

  const hasAthleticAchievements = (school) => {
    if (!school) return false;
    const keys = [
      "athletic_achievement_1",
      "athletic_achievement_2",
      "athletic_achievement_3",
    ];
    return keys.some((key) => {
      const value = school[key];
      if (value === null || value === undefined) return false;
      if (typeof value === "string") return value.trim().length > 0;
      return Boolean(value);
    });
  };

  const hasValidCoords = (school) => {
    if (!school) return false;
    const lat =
      school.latitude ??
      school.coordinates?.latitude ??
      school.location?.latitude;
    const lon =
      school.longitude ??
      school.coordinates?.longitude ??
      school.location?.longitude;
    if (lat === null || lat === undefined || lon === null || lon === undefined) {
      return false;
    }
    const latitude = Number(lat);
    const longitude = Number(lon);
    if (Number.isNaN(latitude) || Number.isNaN(longitude)) return false;
    return true;
  };

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center px-4 py-6"
      style={{ overscrollBehavior: "none" }}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal Container: Flex column layout with constrained max-height */}
      <div className="relative z-10 w-full max-w-[95vw] lg:max-w-[1100px] flex flex-col max-h-[85vh] md:max-h-[90vh] rounded-[32px] border border-[#E7EDF5] bg-[#F9F4EE] shadow-2xl overflow-hidden">
        
        {/* Header (Fixed) */}
        <div className="flex items-start justify-between border-b border-[#E4E6EE] px-6 py-6 shrink-0 bg-[#F9F4EE] z-20">
          <div>
            <p className="text-[12px] font-bold uppercase tracking-[0.2em] text-[#6B7280]">
              Compare
            </p>
            <h2 className="text-[24px] md:text-[32px] font-unbounded text-[#0F172A] mt-1">
              Compare High Schools
            </h2>
            <p className="text-sm text-[#575757] mt-1">
              Side-by-side comparison of school stats, academics, and outcomes.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full bg-white hover:bg-[#F07639] hover:text-white text-[#9CA3AF] transition-all shadow-sm border border-transparent hover:border-[#F07639]"
          >
            <X className="h-6 w-6" strokeWidth={2.5} />
          </button>
        </div>

        {/* Scrollable Body Container */}
        <div className="flex-1 overflow-hidden px-4 md:px-6 py-5 flex flex-col min-h-0">
          <div className="bg-white rounded-[24px] border border-[#E7EDF5] shadow-sm flex flex-col h-full overflow-hidden">
            
            {/* Scroll Area */}
            <div
              className="flex-1 overflow-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent"
              style={{ overscrollBehavior: "contain" }}
            >
              <div className="min-w-[800px] p-4"> {/* Enforces min width for table */}
                {entries.length === 0 ? (
                  <div className="flex h-64 flex-col items-center justify-center gap-2 text-center text-sm text-[#575757]">
                    <p>Select schools to compare up to four high schools.</p>
                  </div>
                ) : (
                  <table className="w-full border-collapse text-left table-fixed">
                    <colgroup>
                      <col className="w-[180px]" /> {/* Label Column */}
                      {entries.map((entry) => (
                        <col key={`col-${entry.school.id}`} />
                      ))}
                    </colgroup>
                    
                    {/* Sticky Table Header */}
                    <thead className="bg-white sticky top-0 z-10 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
                      <tr>
                        <th className="text-[11px] font-satoshi uppercase tracking-[0.35em] text-[#9EA7B9] pb-4 pt-4 pl-2 bg-white align-bottom">
                          Schools
                        </th>
                        {entries.map((entry, columnIndex) => (
                          <th
                            key={`thead-${entry.school.id}`}
                            className={`text-sm font-bold uppercase tracking-[0.05em] text-[#0F172A] pt-4 pb-4 px-3 bg-white align-bottom ${
                              columnIndex > 0 ? "border-l border-[#F0F2F7]" : ""
                            }`}
                          >
                            <div 
                              title={entry.school.school_name}
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {entry.school.school_name ?? "Unnamed school"}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {COMPARISON_SECTIONS.map((section) => (
                        <React.Fragment key={section.id}>
                          <tr>
                            <td
                              colSpan={entries.length + 1}
                              className="pt-6 pb-3 bg-white"
                            >
                              <div className="flex items-center gap-2 sticky left-0">
                                <div className="h-1.5 w-10 rounded-full bg-[#F07639]" />
                                <p className="text-[12px] font-bold uppercase tracking-[0.35em] text-[#6B7280]">
                                  {section.heading}
                                </p>
                                {section.info && <InfoTooltip text={section.info} />}
                              </div>
                            </td>
                          </tr>
                          {section.rows.map((row) => (
                            <tr
                              key={`${section.id}-${row.key}`}
                              className="border-b border-[#F0F2F7] hover:bg-[#FFF8F4] transition-colors"
                            >
                              <td className="text-[11px] font-satoshi uppercase tracking-[0.1em] text-[#9EA7B9] align-top py-3 pl-2 pr-2">
                                {row.label}
                              </td>
                                {entries.map((entry) => (
                                  <td
                                    key={`${entry.school.id}-${section.id}-${row.key}`}
                                    className="text-sm text-[#1F2937] border-l border-[#F0F2F7] px-3 py-3 align-top"
                                  >
                                    {section.id === "athletics" &&
                                      row.key === "topPrograms" &&
                                      !hasAthleticAchievements(entry.school) ? (
                                      <div className="space-y-3">
                                        <div>
                                          <p className="text-sm font-semibold text-[#1F2937]">
                                            No athletic highlights listed yet.
                                          </p>
                                          <p className="mt-1 text-xs text-[#6B7280]">
                                            Explore nearby schools with athletic highlights.
                                          </p>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => onOpenNearbyAthletics?.(entry.school)}
                                          className="compare-tinted-pill"
                                        >
                                          Explore nearby athletics schools
                                        </button>
                                        <p className="text-[11px] text-[#9CA3AF]">
                                          Within 50 miles
                                        </p>
                                      </div>
                                    ) : (
                                      <div className="space-y-2">
                                        {renderComparisonValue(entry.data[row.key], {
                                          numbered:
                                            row.key === "topColleges" ||
                                            row.key === "topPrograms",
                                          clampLines: row.key === "topPrograms" ? 0 : 2,
                                        })}
                                        {section.id === "colleges-majors" &&
                                          row.key === "topMajors" && (
                                            <div className="space-y-1.5">
                                              <button
                                                type="button"
                                                onClick={() => onOpenNearbyStem?.(entry.school)}
                                                disabled={!hasValidCoords(entry.school)}
                                                className="compare-tinted-pill"
                                              >
                                                Explore nearby STEM-focused schools
                                              </button>
                                              <p className="text-[11px] text-[#9CA3AF]">
                                                Top 10 within 50 miles
                                              </p>
                                              {!hasValidCoords(entry.school) && (
                                                <p className="text-[11px] text-[#C1C7D0]">
                                                  Location unavailable.
                                                </p>
                                              )}
                                            </div>
                                          )}
                                      </div>
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ========================================================================
   CARD COMPONENT (UPDATED POSITIONING & SEPARATE BUTTONS)
   ======================================================================== */
const SchoolListCard = ({
  school,
  onSelect,
  compareMode = false,
  isCompareSelected = false,
  compareDisabled = false,
  onCompareToggle,
  onPrefetch,
}) => {
  const cardRef = useRef(null);
  const hasPrefetchedRef = useRef(false);

  useEffect(() => {
    if (!onPrefetch || hasPrefetchedRef.current) return;

    const runPrefetch = () => {
      if (hasPrefetchedRef.current) return;
      hasPrefetchedRef.current = true;
      onPrefetch();
    };

    if (typeof IntersectionObserver === "undefined") {
      runPrefetch();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            runPrefetch();
            observer.disconnect();
          }
        });
      },
      { rootMargin: PREFETCH_ROOT_MARGIN }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    } else {
      runPrefetch();
    }

    return () => observer.disconnect();
  }, [onPrefetch]);

  const snapgradScore = formatSnapgradScore(pickSnapgradRating(school));
  const topMajors =
    Array.isArray(school.top_majors) && school.top_majors.length > 0
      ? school.top_majors
      : Array.isArray(school.metrics?.top_majors)
      ? school.metrics.top_majors
      : [];
  const enrollment = formatScore(pickValue(school, "college_enrollment"), "--");
  const graduation = formatScore(
    school.graduation_rate_percent ?? pickValue(school, "graduation_rate"),
    "--"
  );
  const topColleges = Array.isArray(school.top_colleges)
    ? school.top_colleges
    : [];
  const addressCity = school.address_city ?? school.address?.city ?? "";
  const addressState = school.address_state ?? school.address?.state ?? "";
  const addressZip = school.address_zipcode ?? school.address?.zipcode ?? "";
  const displayName = school.school_name
    ? school.school_name.toUpperCase()
    : "Unnamed School";
  const distanceLabel =
    school.distance_label ||
    (typeof school.distance_miles === "number"
      ? `${school.distance_miles.toFixed(1)} mi away`
      : null);
  const locationParts = [
    addressCity ? addressCity.toUpperCase() : null,
    addressState ? addressState.toUpperCase() : null,
    addressZip,
  ].filter(Boolean);
  const locationLabel = locationParts.join(", ");
  const comparisonButtonLabel = compareDisabled
    ? "Maximum comparison limit reached"
    : isCompareSelected
    ? "Remove school from comparison"
    : "Add school to comparison";

  const handleCompareButtonClick = (event) => {
    event.stopPropagation();
    if (compareDisabled) return;
    onCompareToggle?.();
  };

  const handleCardClick = (event) => {
    if (!compareMode) return;

    const interactive = event.target?.closest?.(
      "button, a, input, textarea, select, [role='button']"
    );
    if (interactive) return;

    if (compareDisabled) return;
    onCompareToggle?.();
  };

  const handlePrefetch = () => {
    if (hasPrefetchedRef.current) return;
    onPrefetch?.();
    hasPrefetchedRef.current = true;
  };

  return (
    <article
      ref={cardRef}
      onMouseEnter={handlePrefetch}
      onFocus={handlePrefetch}
      onClick={handleCardClick}
      className="relative bg-white border border-[#E7EDF5] rounded-[22px] p-5 shadow-sm hover:shadow-md transition-shadow h-full flex flex-col"
    >
      {/* Removed the overlay button that used to be here */}
      <div className="flex flex-col gap-4 flex-1">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1 min-w-0 space-y-1">
            <h3
              className="font-unbounded text-[20px] lg:text-[22px] text-black leading-tight tracking-[0.01em] line-clamp-3 min-h-[4.6rem] md:line-clamp-2 md:min-h-[3.5rem]"
              title={displayName}
            >
              {displayName}
            </h3>
            <p className="flex items-center gap-1.5 font-satoshi text-[13px] text-[#6C7A91] truncate mt-0.5 md:mt-0">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">
                {locationLabel || "Location unavailable"}
              </span>
              {distanceLabel && (
                <span className="text-[12px] text-[#9CA3AF] whitespace-nowrap">
                  - {distanceLabel}
                </span>
              )}
            </p>
            {school.nearest_to_search && (
              <span className="mt-1 inline-flex items-center rounded-full bg-[#FFF3ED] px-2 py-0.5 text-[11px] font-semibold text-[#C2410C]">
                Nearest to your search
              </span>
            )}
          </div>
          {/* Right Side: Stacked Badges & Compare */}
          <div className="shrink-0 flex items-start gap-2">
            <div className="flex flex-col items-end gap-2">
                <SnapScoreBadge score={snapgradScore} />
                {school.sat_average && (
                    <SatScoreBadge score={formatScore(school.sat_average)} />
                )}
            </div>

            {/* Comparison Toggle (ONLY SHOW IF compareMode is TRUE) */}
            {compareMode && (
              <button
                type="button"
                onClick={handleCompareButtonClick}
                disabled={compareDisabled}
                className={`mt-1 mr-1 flex h-12 w-12 items-center justify-center rounded-full border shadow-sm transition-colors ${
                  isCompareSelected
                    ? "bg-[#F07639] border-[#F07639] text-white"
                    : compareDisabled
                    ? "bg-gray-100 border-gray-200 text-gray-300"
                    : "bg-white border-[#E4EAF2] text-[#9CA3AF] hover:border-[#F07639] hover:text-[#F07639]"
                }`}
                aria-label={comparisonButtonLabel}
                title={comparisonButtonLabel}
              >
                <Check className="w-5 h-5" strokeWidth={3} />
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 flex-1 items-start">
          <TopCollegesBlock colleges={topColleges} />
          <TopMajorsBlock majors={topMajors} />
          <MetricBlock
            label="College enrollment"
            value={enrollment}
            suffix="%"
          />
          <MetricBlock label="Graduation rate" value={graduation} suffix="%" />
        </div>

        {!compareMode && (
          <div className="flex items-center justify-end mt-auto">
            <button
              type="button"
              onClick={onSelect}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F77B1A] text-white font-unbounded text-[14px] hover:bg-[#E86D0A] transition-colors shadow-[0_4px_10px_rgba(247,123,26,0.2)]"
            >
              View Details
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </article>
  );
};

const TopCollegesBlock = ({ colleges }) => {
  const [logoErrors, setLogoErrors] = useState({});
  const topThree = Array.isArray(colleges)
    ? colleges.filter(Boolean).slice(0, 3)
    : [];
  const initials = (name = "") =>
    name.split(/\s+/).filter(Boolean).map((w) => w[0]?.toUpperCase()).join("").slice(0, 2) || "?";

  useEffect(() => {
    setLogoErrors({});
  }, [colleges]);

  if (topThree.length === 0) {
    return <MetricBlock label="Top Colleges" value="--" accent />;
  }

  return (
    <div className="relative rounded-[16px] border border-[#F4E4BF] p-3 sm:p-3.5 bg-[#FFF4E0] flex flex-col justify-between min-h-[132px] sm:min-h-[140px] overflow-visible">
      <div className="flex justify-between items-start mb-2">
        <p className="font-satoshi text-[13px] font-medium text-[#4B5563]">
          Top Colleges
        </p>
        <InfoTooltip text="The Colleges where the students of this school are most interested to attend after graduating." />
      </div>

      <div className="flex items-end justify-between gap-1.5 sm:gap-2 px-0.5 sm:px-1">
        {topThree.map((college, index) => {
          const rank = college?.rank ?? index + 1;
          const collegeName = college?.name ?? "Unknown college";
          const { logoSrc, imgClassName } = resolveCollegeLogo(college, {
            fallback: COLLEGE_LOGO_FALLBACK,
          });
          const key = college?.ipeds ?? collegeName ?? index;
          const hasLogo =
            Boolean(logoSrc && logoSrc !== COLLEGE_LOGO_FALLBACK) &&
            !logoErrors[key];

          return (
            <div
              key={`${collegeName}-${college?.ipeds ?? index}`}
              className="relative group flex flex-col items-center gap-1 sm:gap-1.5"
              title={collegeName}
              aria-label={collegeName}
              tabIndex={0}
            >
              <div className="relative w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-white flex items-center justify-center overflow-hidden border border-[#F0CB82] shadow-sm p-1">
                {!hasLogo && (
                  <span className="font-unbounded text-[12px] sm:text-[14px] text-[#C46609]">
                    {initials(collegeName)}
                  </span>
                )}
                {hasLogo && (
                  <img
                    src={logoSrc}
                    alt={`${collegeName} logo`}
                    className={`${imgClassName} absolute inset-0 w-full h-full object-contain`}
                    onError={(event) => {
                      console.warn("[logo-error]", {
                        name: collegeName,
                        src: event.currentTarget.src,
                      });
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = COLLEGE_LOGO_FALLBACK;
                      setLogoErrors((prev) => ({ ...prev, [key]: true }));
                    }}
                  />
                )}
              </div>
              <span className="text-[10px] sm:text-[11px] font-unbounded text-[#6B7280]">
                #{rank}
              </span>
              <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-0 whitespace-nowrap rounded-md bg-black text-white text-[11px] px-2 py-1 opacity-0 group-hover:opacity-90 group-focus-within:opacity-90 transition-opacity z-30">
                {collegeName}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TopMajorsBlock = ({ majors }) => {
  const normalizeMajorName = (value) =>
    (value || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const topThree = (() => {
    if (!Array.isArray(majors)) return [];
    const unique = [];
    const seen = new Set();
    for (const major of majors) {
      if (!major) continue;
      const name = typeof major === "string" ? major : major?.name;
      const normalized = normalizeMajorName(name);
      if (!normalized || seen.has(normalized)) continue;
      seen.add(normalized);
      unique.push(major);
      if (unique.length >= 3) break;
    }
    return unique;
  })();

  if (topThree.length === 0) {
    return <MetricBlock label="Top Majors" value="--" accent />;
  }

  return (
    <div className="relative rounded-[16px] border border-[#F4E4BF] p-3.5 bg-[#FFF4E0] flex flex-col min-h-[140px]">
      <div className="flex justify-between items-start mb-3">
        <p className="font-satoshi text-[13px] font-medium text-[#4B5563]">
          Top Majors
        </p>
        <InfoTooltip text="The Majors which the students of this school are most interested in pursuing." />
      </div>

      <div className="flex-1 flex flex-col justify-center gap-2.5">
        {topThree.map((major, index) => {
          const rank = index + 1;
          const name =
            (typeof major === "string" ? major : major?.name) ?? "Unknown major";
          return (
            <div
              key={`${name}-${major?.cip_code ?? rank}`}
              className="flex items-center gap-2 w-full"
              title={name}
            >
              <span className="text-[#F07639] text-[12px] font-unbounded shrink-0">
                #{rank}
              </span>
              <span className="font-satoshi text-[13px] text-[#1F2937] truncate">
                {name}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SnapScoreBadge = ({ score }) => (
  <div className="group/snap relative inline-flex items-center justify-between gap-3 rounded-[14px] border border-[#F2CDA1] bg-[#FFFBF7] px-3 py-1.5 shadow-sm min-w-[135px]">
    <span className="font-satoshi text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF] leading-none whitespace-nowrap">
      Rating
    </span>
    <span className="font-unbounded text-[20px] text-[#F07639] leading-none">
      {score ?? "--"}
      {score != null && score !== "--" && (
        <span className="text-[12px] text-[#F0A56E] font-semibold align-top ml-1">
          /10
        </span>
      )}
    </span>
    {/* Tooltip */}
    <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-10 w-[240px] rounded-lg bg-black/90 px-3 py-2 text-left text-[11px] leading-snug text-white opacity-0 transition-opacity group-hover/snap:opacity-100 group-focus-within/snap:opacity-100">
      Snapgrad score reflects the school rating on a 1 to 10 scale.
    </span>
  </div>
);

const SatScoreBadge = ({ score }) => (
    <div className="group/sat relative inline-flex items-center justify-between gap-3 rounded-[14px] border border-[#E7EDF5] bg-white px-3 py-1.5 shadow-sm min-w-[135px]">
      <span className="font-satoshi text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF] leading-none whitespace-nowrap">
        SAT AVG
      </span>
      <span className="font-unbounded text-[18px] text-[#0F172A] leading-none">
        {score ?? "--"}
      </span>
    </div>
  );

const MetricBlock = ({ label, value, suffix = "", accent = false }) => (
  <div
    className={`rounded-[16px] border px-4 py-3 bg-[#FFF4E0] min-h-[90px] ${
      accent ? "border-[#F4E4BF]" : "border-[#E7EDF5]"
    } flex flex-col h-full justify-between`}
  >
    <p className="font-satoshi text-[13px] font-medium text-[#4B5563] leading-tight">
      {label}
    </p>
    <p
      className={`font-unbounded text-[32px] ${
        accent ? "text-[#EA580C]" : "text-[#0F172A]"
      }`}
    >
      {value}
      <span className="text-[20px] ml-0.5 text-[#64748B] font-satoshi">
        {value !== "--" && suffix}
      </span>
    </p>
  </div>
);

const InfoTooltip = ({ text }) => (
  <button
    type="button"
    className="group/info relative flex h-4 w-4 items-center justify-center rounded-full text-[#9CA3AF] hover:text-[#F07639] focus:outline-none"
  >
    <Info className="h-3.5 w-3.5" strokeWidth={2} />
    <span className="pointer-events-none absolute right-0 top-6 z-10 w-[200px] rounded-lg bg-black/90 px-3 py-2 text-left text-[11px] leading-snug text-white opacity-0 transition-opacity group-hover/info:opacity-100">
      {text}
    </span>
  </button>
);

export default SchoolsSection;
